/**
 * PremiumManagementPageV2 — bulk premium-grant tool, V2 port.
 *
 * Same business logic as v1 (parseUserList / handleProcess /
 * handleValidate / handleRetryValidation / per-row validateRow on
 * blur / dedup by lowercased email). UI primitives swapped to V2:
 *
 *   • PageHeader with a ghost "View history" button.
 *   • FormSection 01 — bulk-grant table: action button row + LibTable
 *     with inline TextInputs / Select per row, StatusPill, collapsible
 *     error sub-row.
 *   • FormSection 02 — Instructions (collapsible RailCard).
 *   • FormSection 03 — Load multiple users (collapsible RailCard
 *     containing paste-textarea + Upload CSV).
 *
 * PremiumGrantHistoryModal is kept verbatim — the page-level chrome
 * change is what matters here; a deeper restyle of the history modal
 * is a follow-up.
 */
import React, { useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { toast } from '../../../../utils/v2Toast';
import {
  bulkGrantPremium,
  validateBulkPremiumUsers,
} from '../../../../services/userServices';
import PremiumGrantHistoryModal from './PremiumGrantHistoryModalV2';
import {
  PageHeader,
  Button,
  LibTable,
  StatusPill,
  IconButton,
  cn,
} from '../../../redesign';
import {
  FormSection,
  TextInput,
  Textarea,
  Select,
  RailCard,
  RailCardHead,
} from '../../../redesign/edit';

const DURATION_OPTIONS = ['1wk', '2wk', '1mo', '3mo', '6mo', '1yr', 'lifetime'];
const PROCESS_BATCH_SIZE = 25;
const VALIDATE_BATCH_SIZE = 50;

const PlusSign = (
  <span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>+</span>
);
const HistoryIcon = (
  // Material-style history glyph: clock face with a CCW rewind arrow
  // wrapping its left side. Filled path so it sits at the same visual
  // weight as the coral Process button's send icon next to it.
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z" />
  </svg>
);
const SendIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);
const TrashIcon = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);
const UploadIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);
const ExpandIcon = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const STATUS_DISPLAY = {
  success:           { tone: 'green',  label: 'Success' },
  existing:          { tone: 'green',  label: 'Pending grant only' },
  existing_premium:  { tone: 'blue',   label: 'Has premium · extend' },
  needs_account:     { tone: 'amber',  label: 'Pending create account' },
  created:           { tone: 'green',  label: 'Created' },
  error:             { tone: 'coral',  label: 'Error' },
  validation_failed: { tone: 'amber',  label: 'Validation failed' },
  pending:           { tone: 'gray',   label: 'Pending' },
};

function makeEmptyRow() {
  return {
    id: crypto.randomUUID(),
    email: '',
    name: '',
    duration: '1yr',
    reason: '',
    braze_tag: '',
    status: 'pending',
    error: null,
    showError: false,
  };
}

export default function PremiumManagementPageV2() {
  const [rows, setRows] = useState([makeEmptyRow()]);
  const [processing, setProcessing] = useState(false);
  const [progressText, setProgressText] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [originalRows, setOriginalRows] = useState([]);
  const [inputList, setInputList] = useState('');
  const fileInputRef = useRef();
  const globalReason = '';

  /* ─── Bulk process ─────────────────────────────────────────── */
  const handleProcess = async () => {
    const rowsToProcess = rows.filter((r) => !['success', 'created'].includes(r.status));
    if (rowsToProcess.length === 0) {
      Swal.fire({ icon: 'info', title: 'Nothing to process', text: 'All rows have been processed successfully.' });
      return;
    }

    const invalidRows = rowsToProcess
      .map((row, index) => ({
        index,
        missingFields: [
          !row.email?.trim() ? 'Email' : null,
          !['existing', 'existing_premium'].includes(row.status) && !row.name?.trim() ? 'Name' : null,
          !row.duration?.trim() ? 'Duration' : null,
          !row.reason?.trim() ? 'Reason' : null,
        ].filter(Boolean),
      }))
      .filter((r) => r.missingFields.length > 0);

    if (invalidRows.length > 0) {
      setRows((prev) =>
        prev.map((row) => {
          const rowIndex = rowsToProcess.findIndex((r) => r.email === row.email);
          const invalid = rowIndex !== -1 ? invalidRows.find((r) => r.index === rowIndex) : null;
          if (invalid) {
            return {
              ...row,
              status: 'error',
              error: `Required fields missing: ${invalid.missingFields.join(', ')}`,
              showError: true,
            };
          }
          return row;
        }),
      );
      Swal.fire({
        icon: 'error',
        title: 'Validation error',
        text: `Please fill in all required fields. ${invalidRows.length} row(s) have missing information.`,
      });
      return;
    }

    setProcessing(true);
    const usersToSend = rowsToProcess.map(({ email, name, duration, reason, braze_tag }) => ({
      email, name, duration, reason, braze_tag,
    }));
    const totalBatches = Math.ceil(usersToSend.length / PROCESS_BATCH_SIZE);
    const statusByEmail = new Map(
      rowsToProcess.map((r) => [r.email.toLowerCase(), { status: r.status, error: r.error }]),
    );

    for (let i = 0; i < totalBatches; i++) {
      const batch = usersToSend.slice(i * PROCESS_BATCH_SIZE, (i + 1) * PROCESS_BATCH_SIZE);
      setProgressText(`Processing batch ${i + 1}/${totalBatches}…`);
      try {
        const resp = await bulkGrantPremium(batch);
        const results = Array.isArray(resp?.objects)
          ? resp.objects
          : Array.isArray(resp?.data)
            ? resp.data
            : Array.isArray(resp)
              ? resp
              : [];
        for (const r of results) {
          const key = (r.email || '').toLowerCase();
          const existing = statusByEmail.get(key);
          if (existing && ['success', 'created'].includes(existing.status)) continue;
          statusByEmail.set(key, { status: r.status, error: r.error ?? null });
        }
        setRows((prev) =>
          prev.map((row) => {
            const match = results.find(
              (it) => (it.email || '').toLowerCase() === row.email.toLowerCase(),
            );
            if (!match || ['success', 'created'].includes(row.status)) return row;
            return { ...row, status: match.status, error: match.error ?? null };
          }),
        );
      } catch {
        const batchEmails = new Set(batch.map((u) => u.email.toLowerCase()));
        for (const email of batchEmails) {
          const existing = statusByEmail.get(email);
          if (existing && ['success', 'created'].includes(existing.status)) continue;
          statusByEmail.set(email, { status: 'error', error: 'Request failed — please retry this batch' });
        }
        setRows((prev) =>
          prev.map((row) => {
            if (!batchEmails.has(row.email.toLowerCase()) || ['success', 'created'].includes(row.status)) return row;
            return { ...row, status: 'error', error: 'Request failed — please retry this batch' };
          }),
        );
      }
    }

    setProcessing(false);
    setProgressText('');

    let granted = 0, created = 0, errors = 0, validationFailed = 0;
    for (const { status } of statusByEmail.values()) {
      if (status === 'success') granted++;
      else if (status === 'created') created++;
      else if (status === 'error') errors++;
      else if (status === 'validation_failed') validationFailed++;
    }
    const parts = [];
    if (granted > 0) parts.push(`${granted} granted`);
    if (created > 0) parts.push(`${created} created`);
    if (errors > 0) parts.push(`${errors} errors`);
    if (validationFailed > 0) parts.push(`${validationFailed} still need validation`);
    const hasIssues = errors > 0 || validationFailed > 0;
    Swal.fire({
      icon: hasIssues ? 'warning' : 'success',
      title: 'Processing complete',
      text: parts.join(', ') || 'No users processed.',
      footer: hasIssues
        ? 'Errored or unvalidated rows stayed in the table — fix and click Process again to retry only those.'
        : undefined,
    });
  };

  /* ─── Row helpers ──────────────────────────────────────────── */
  const handleAddRow = () => {
    setRows((prev) => [...prev, makeEmptyRow()]);
  };
  const handleRemoveRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };
  const handleFieldChange = (index, field) => (e) => {
    const value = e.target.value;
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };
  const handleClearAll = () => setRows([]);
  const handleClearSuccessful = () =>
    setRows((prev) => prev.filter((r) => !['success', 'created'].includes(r.status)));

  const validateRow = async (rowIdx) => {
    const row = rows[rowIdx];
    if (!row) return;
    try {
      const resp = await validateBulkPremiumUsers([
        { email: row.email, name: row.name, duration: row.duration, reason: row.reason },
      ]);
      const results = Array.isArray(resp?.objects) ? resp.objects
        : Array.isArray(resp?.data) ? resp.data
        : Array.isArray(resp) ? resp : [];
      const result = results[0] || null;
      if (result) {
        setRows((prev) =>
          prev.map((r, i) =>
            i === rowIdx ? { ...r, status: result.status, error: result.error ?? null } : r,
          ),
        );
      }
    } catch {
      // keep as-is on validation failure
    }
  };

  /* ─── Retry validation ─────────────────────────────────────── */
  const handleRetryValidation = async () => {
    const failedRows = rows.filter(
      (r) => ['validation_failed', 'pending'].includes(r.status) && r.email?.trim(),
    );
    if (failedRows.length === 0) return;
    setProcessing(true);
    setProgressText('Retrying validation…');
    try {
      const usersToValidate = failedRows.map((r) => ({
        email: r.email, name: r.name, duration: r.duration, reason: r.reason,
      }));
      let networkFailedCount = 0;
      let validationErrorCount = 0;
      let validatedCount = 0;
      for (let i = 0; i < usersToValidate.length; i += VALIDATE_BATCH_SIZE) {
        const batch = usersToValidate.slice(i, i + VALIDATE_BATCH_SIZE);
        try {
          const resp = await validateBulkPremiumUsers(batch);
          const results = Array.isArray(resp?.objects) ? resp.objects
            : Array.isArray(resp?.data) ? resp.data
            : Array.isArray(resp) ? resp : [];
          for (const r of results) {
            if (r.status === 'error') validationErrorCount++;
            else validatedCount++;
          }
          setRows((prev) =>
            prev.map((row) => {
              const match = results.find(
                (it) => (it.email || '').toLowerCase() === row.email.toLowerCase(),
              );
              if (!match || ['success', 'created'].includes(row.status)) return row;
              return { ...row, status: match.status, error: match.error ?? null };
            }),
          );
        } catch (err) {
          const batchEmails = new Set(batch.map((u) => u.email.toLowerCase()));
          networkFailedCount += batch.length;
          setRows((prev) =>
            prev.map((row) => {
              if (!batchEmails.has(row.email.toLowerCase()) || ['success', 'created'].includes(row.status)) return row;
              return {
                ...row,
                status: 'validation_failed',
                error: err?.message || 'Validation request failed — try again or check your connection.',
              };
            }),
          );
        }
      }
      const hasIssues = networkFailedCount > 0 || validationErrorCount > 0;
      const parts = [];
      if (validatedCount > 0) parts.push(`${validatedCount} validated`);
      if (validationErrorCount > 0) parts.push(`${validationErrorCount} rejected by server`);
      if (networkFailedCount > 0) parts.push(`${networkFailedCount} couldn't reach the server`);
      Swal.fire({
        icon: hasIssues ? 'warning' : 'success',
        title: hasIssues ? 'Validation retry finished with issues' : 'Validation retry complete',
        text: parts.join(', ') || 'No rows retried.',
        footer: networkFailedCount > 0
          ? "Rows that couldn't reach the server stayed as Validation Failed — click Retry Validation again to try once more."
          : validationErrorCount > 0
            ? 'Rows marked Error were rejected by the server — open the row to see why.'
            : undefined,
      });
    } finally {
      setProcessing(false);
      setProgressText('');
    }
  };

  /* ─── Paste / CSV import ───────────────────────────────────── */
  const parseUserList = async (text) => {
    const lines = text.trim().split(/\r?\n/);
    let newData = [];
    for (const line of lines) {
      if (!line.trim()) continue;
      const parts = line.split(',').map((v) => v.trim());
      let email = '', name = '', duration = '1yr', reason = globalReason, braze_tag = '';
      if (parts.length >= 1) email = parts[0];
      if (parts.length >= 2) name = parts[1] || email.split('@')[0].replace(/\./g, ' ');
      else name = email.split('@')[0].replace(/\./g, ' ');
      if (parts.length >= 3) duration = parts[2] || '1yr';
      if (parts.length >= 4) reason = parts[3] || globalReason;
      if (parts.length >= 5) braze_tag = parts[4] || '';
      if (!email || email.toLowerCase() === 'user_email') continue;
      newData.push({
        id: crypto.randomUUID(),
        email, name, duration, reason, braze_tag,
        status: 'pending', error: null, showError: false,
      });
    }
    // Dedup
    const seen = new Set();
    const deduped = [];
    let duplicateCount = 0;
    for (const row of newData) {
      const key = row.email.toLowerCase();
      if (seen.has(key)) { duplicateCount++; continue; }
      seen.add(key);
      deduped.push(row);
    }
    newData = deduped;
    if (duplicateCount > 0) {
      toast.info(
        `${duplicateCount} duplicate ${duplicateCount === 1 ? 'email' : 'emails'} merged`,
        { timer: 4000 },
      );
    }
    // Validate in batches
    if (newData.length > 0) {
      const usersToValidate = newData.map(({ email, name, duration, reason }) => ({
        email, name, duration, reason,
      }));
      for (let i = 0; i < usersToValidate.length; i += VALIDATE_BATCH_SIZE) {
        const batch = usersToValidate.slice(i, i + VALIDATE_BATCH_SIZE);
        try {
          const resp = await validateBulkPremiumUsers(batch);
          const results = Array.isArray(resp?.objects) ? resp.objects
            : Array.isArray(resp?.data) ? resp.data
            : Array.isArray(resp) ? resp : [];
          for (const r of results) {
            const match = newData.find((row) => row.email.toLowerCase() === (r.email || '').toLowerCase());
            if (match) {
              match.status = r.status;
              match.error = r.error ?? null;
            }
          }
        } catch (err) {
          const batchEmails = new Set(batch.map((u) => u.email.toLowerCase()));
          for (const row of newData) {
            if (batchEmails.has(row.email.toLowerCase())) {
              row.status = 'validation_failed';
              row.error = err?.message || 'Could not reach the server. Click "Retry validation" to try again.';
            }
          }
        }
      }
    }
    // Merge into table
    const isEmptyRow = (row) => !row.email && !row.name && !row.reason && !row.braze_tag && row.duration === '1yr' && row.status === 'pending';
    setRows((prev) => {
      const filteredPrev = prev.filter((row) => !isEmptyRow(row));
      const emailMap = new Map(filteredPrev.map((r) => [r.email.toLowerCase(), r]));
      newData.forEach((newRow) => {
        const key = newRow.email.toLowerCase();
        if (emailMap.has(key)) Object.assign(emailMap.get(key), newRow);
        else emailMap.set(key, newRow);
      });
      return Array.from(emailMap.values());
    });
    setOriginalRows((prev) => {
      const filteredPrev = prev.filter((row) => !isEmptyRow(row));
      const emailMap = new Map(filteredPrev.map((r) => [r.email.toLowerCase(), r]));
      newData.forEach((newRow) => {
        const key = newRow.email.toLowerCase();
        if (emailMap.has(key)) Object.assign(emailMap.get(key), newRow);
        else emailMap.set(key, newRow);
      });
      return Array.from(emailMap.values());
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (evt) => parseUserList(evt.target.result);
      reader.readAsText(file);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleLoadInputList = () => {
    if (inputList.trim()) {
      parseUserList(inputList);
      setInputList('');
    }
  };

  const hasValidationFailedRows = rows.some((r) => r.status === 'validation_failed');
  const hasSuccessfulRows = rows.some((r) => ['success', 'created'].includes(r.status));

  return (
    <div className="bg-bg min-h-full">
      <PageHeader
        eyebrow="Users"
        title="Premium Access Requests"
        total={`${rows.length} rows`}
        actions={
          <Button
            variant="ghost"
            leadingIcon={HistoryIcon}
            onClick={() => setHistoryOpen(true)}
          >
            View history
          </Button>
        }
      />

      <div style={{ padding: '0 28px 60px', maxWidth: 1280 }}>
        <FormSection
          title="Bulk grant table"
          num="01"
          right={
            <div className="flex items-center" style={{ gap: 6, flexWrap: 'wrap' }}>
              <Button variant="ghost" leadingIcon={PlusSign} onClick={handleAddRow}>
                Add row
              </Button>
              <Button
                variant="ghost"
                onClick={handleClearAll}
                disabled={rows.length === 0}
              >
                Clear all
              </Button>
              <Button
                variant="ghost"
                onClick={handleClearSuccessful}
                disabled={!hasSuccessfulRows}
              >
                Clear successful
              </Button>
              {hasValidationFailedRows && (
                <Button
                  variant="ghost"
                  onClick={handleRetryValidation}
                  disabled={processing}
                >
                  Retry validation
                </Button>
              )}
              <Button
                variant="coral"
                leadingIcon={SendIcon}
                onClick={handleProcess}
                disabled={processing || rows.length === 0}
              >
                {processing ? (progressText || 'Processing…') : 'Process'}
              </Button>
            </div>
          }
        >
          {rows.length === 0 ? (
            <div
              className="bg-surface border border-sand text-text-muted italic text-center"
              style={{ borderRadius: 12, padding: '32px 16px', fontSize: 13 }}
            >
              No rows. Add a row manually or upload a CSV in the section below.
            </div>
          ) : (
            <LibTable
              fixed
              className="border border-sand"
              style={{ borderRadius: 12, overflow: 'hidden' }}
            >
              <colgroup>
                {/* email */}
                <col style={{ width: '22%' }} />
                {/* name */}
                <col style={{ width: '16%' }} />
                {/* duration */}
                <col style={{ width: 110 }} />
                {/* reason */}
                <col />
                {/* braze tag */}
                <col style={{ width: '14%' }} />
                {/* status */}
                <col style={{ width: 200 }} />
                {/* remove */}
                <col style={{ width: 60 }} />
              </colgroup>
              <LibTable.Head>
                <tr>
                  <LibTable.Th>Email</LibTable.Th>
                  <LibTable.Th>Name</LibTable.Th>
                  <LibTable.Th>Duration</LibTable.Th>
                  <LibTable.Th>Reason</LibTable.Th>
                  <LibTable.Th>Braze tag</LibTable.Th>
                  <LibTable.Th>Status</LibTable.Th>
                  <LibTable.Th />
                </tr>
              </LibTable.Head>
              <tbody>
                {rows.map((row, idx) => {
                  const display = STATUS_DISPLAY[row.status] || STATUS_DISPLAY.pending;
                  const hasError = ['error', 'validation_failed'].includes(row.status) && row.error;
                  return (
                    <React.Fragment key={row.id}>
                      <LibTable.Row>
                        <LibTable.Td>
                          <TextInput
                            value={row.email}
                            onChange={handleFieldChange(idx, 'email')}
                            onBlur={() => validateRow(idx)}
                            autoComplete="off"
                          />
                        </LibTable.Td>
                        <LibTable.Td>
                          <TextInput
                            value={row.name}
                            onChange={handleFieldChange(idx, 'name')}
                            autoComplete="off"
                          />
                        </LibTable.Td>
                        <LibTable.Td>
                          <Select
                            value={row.duration}
                            onChange={handleFieldChange(idx, 'duration')}
                          >
                            {DURATION_OPTIONS.map((d) => (
                              <option key={d} value={d}>{d}</option>
                            ))}
                          </Select>
                        </LibTable.Td>
                        <LibTable.Td>
                          <TextInput
                            value={row.reason || ''}
                            onChange={handleFieldChange(idx, 'reason')}
                            autoComplete="off"
                          />
                        </LibTable.Td>
                        <LibTable.Td>
                          <TextInput
                            value={row.braze_tag || ''}
                            onChange={handleFieldChange(idx, 'braze_tag')}
                            autoComplete="off"
                          />
                        </LibTable.Td>
                        <LibTable.Td>
                          <div className="flex items-center" style={{ gap: 6 }}>
                            <StatusPill tone={display.tone}>{display.label}</StatusPill>
                            {hasError && (
                              <button
                                type="button"
                                onClick={() =>
                                  setRows((prev) => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], showError: !next[idx].showError };
                                    return next;
                                  })
                                }
                                className="text-coral"
                                style={{
                                  background: 'transparent',
                                  border: 0,
                                  cursor: 'pointer',
                                  display: 'inline-grid',
                                  placeItems: 'center',
                                  width: 22,
                                  height: 22,
                                  padding: 0,
                                  transform: row.showError ? 'rotate(180deg)' : 'rotate(0)',
                                  transition: 'transform 160ms ease',
                                }}
                                aria-label="Toggle error detail"
                              >
                                {ExpandIcon}
                              </button>
                            )}
                          </div>
                        </LibTable.Td>
                        <LibTable.Td style={{ textAlign: 'center' }}>
                          <IconButton
                            size="sm"
                            aria-label="Remove row"
                            title="Remove row"
                            onClick={() => handleRemoveRow(idx)}
                          >
                            {TrashIcon}
                          </IconButton>
                        </LibTable.Td>
                      </LibTable.Row>
                      {hasError && row.showError && (
                        <tr>
                          <td colSpan={7} style={{ padding: 0, border: 0 }}>
                            <div
                              className="border-l-4 border-coral"
                              style={{
                                background: '#FFF3EE',
                                padding: '8px 14px',
                                fontSize: 12,
                                color: '#8A4A35',
                                fontFamily: 'inherit',
                              }}
                            >
                              {row.error}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </LibTable>
          )}
        </FormSection>

        {/* 02 Instructions */}
        <FormSection title="Instructions" num="02">
          <RailCard
            defaultOpen={true}
            head={({ open, toggle }) => (
              <RailCardHead
                eyebrow="How to use this tool"
                open={open}
                onToggle={toggle}
              />
            )}
          >
            <div style={{ padding: 14, fontSize: 13, lineHeight: 1.55, color: '#2C3E50' }}>
              <p style={{ marginTop: 0 }}>
                <strong>Step 1:</strong> Add individual users to the table above, OR use the section below to load multiple users at once.
              </p>
              <ul style={{ paddingLeft: 22, color: '#7A8694' }}>
                <li><strong className="text-navy">Email</strong> — required.</li>
                <li><strong className="text-navy">Name</strong> — first name or full name.</li>
                <li><strong className="text-navy">Duration</strong> — required; access auto-expires after the chosen window.</li>
                <li><strong className="text-navy">Reason</strong> — short description for why access is being given.</li>
                <li><strong className="text-navy">Braze tag</strong> — optional; sent as a Braze user property if provided.</li>
              </ul>
              <p>
                <strong>Step 2:</strong> Click <strong>Process</strong> in the table header above.
              </p>
              <ul style={{ paddingLeft: 22, color: '#7A8694' }}>
                <li>Successful rows get a green <strong className="text-navy">Success</strong> pill.</li>
                <li>Red <strong className="text-navy">Error</strong> rows can be fixed and reprocessed — successful rows aren't re-sent.</li>
                <li>Amber <strong className="text-navy">Validation Failed</strong> = network failure during the pre-process check. Click <strong>Retry Validation</strong> to re-run.</li>
                <li>Duplicate emails are merged on import — a toast shows how many were collapsed.</li>
              </ul>
            </div>
          </RailCard>
        </FormSection>

        {/* 03 Bulk import */}
        <FormSection title="Load multiple users at once" num="03">
          <RailCard
            defaultOpen={false}
            head={({ open, toggle }) => (
              <RailCardHead
                eyebrow="Paste list or upload CSV"
                open={open}
                onToggle={toggle}
              />
            )}
          >
            <div style={{ padding: 14 }}>
              <p
                className="text-text-muted"
                style={{ fontSize: 12.5, marginTop: 0, marginBottom: 12, lineHeight: 1.5 }}
              >
                Paste one user per line, or upload a CSV. Available columns: Email, Name, Duration, Reason, Braze Tag.
              </p>

              <div
                className="grid grid-cols-1 md:grid-cols-[1fr_140px]"
                style={{ gap: 12, alignItems: 'flex-start' }}
              >
                <Textarea
                  value={inputList}
                  onChange={(e) => setInputList(e.target.value)}
                  rows={4}
                  placeholder={'john@doe.com\njane@smith.com,Jane Smith\nbob@bob.com,Bob,1yr,Special promo\nalice@alice.com,Alice,6mo,Marketing campaign,tag1'}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Button
                    variant="coral"
                    onClick={handleLoadInputList}
                    disabled={!inputList.trim()}
                  >
                    Load
                  </Button>
                  <label className={cn('inline-flex')}>
                    <Button
                      variant="ghost"
                      leadingIcon={UploadIcon}
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Upload CSV
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </div>

              <div
                className="text-text-muted"
                style={{ fontSize: 11.5, marginTop: 12, lineHeight: 1.6 }}
              >
                <strong>Format examples</strong> (comma separated):<br />
                Basic: <code className="font-mono text-navy">john@doe.com</code><br />
                With name: <code className="font-mono text-navy">jane@smith.com,Jane Smith</code><br />
                With duration: <code className="font-mono text-navy">bob@bob.com,Bob,6mo</code><br />
                Complete: <code className="font-mono text-navy">alice@alice.com,Alice,1yr,Special promo,tag1</code><br />
                Available durations: <code className="font-mono text-navy">{DURATION_OPTIONS.join(', ')}</code> (defaults to <code className="font-mono text-navy">1yr</code>).
              </div>
            </div>
          </RailCard>
        </FormSection>
      </div>

      <PremiumGrantHistoryModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
    </div>
  );
}
